// Guard Web is the fallback landing surface for Supabase Auth invite / recovery links when the
// native app is not installed (product lock 2026-08-07, 1.3/F1: "If the app is not installed, use
// Guard Web (or hosted auth page) to complete password, then prompt to open TF install").
//
// Supabase's verify endpoint bounces the officer back to the redirect host carrying either
// implicit-flow tokens in the URL fragment (#access_token=...&type=invite) or a PKCE code in the
// query (?code=...), and carries failures the same way (#error=...&error_code=otp_expired).
//
// This module is PURE parsing plus one module-load snapshot. The snapshot matters: supabase-js
// strips the fragment as soon as it consumes it, so the purpose ("was this an invite?") has to be
// read before the client is constructed. Nothing here reads or stores a token - only the reason
// the officer arrived.

export type AuthCallbackPurpose = "invite" | "recovery" | "signup" | "magiclink" | "unknown";

export type AuthCallback =
  | { readonly kind: "none" }
  | { readonly kind: "session"; readonly purpose: AuthCallbackPurpose }
  | { readonly kind: "error"; readonly reason: string };

/** Expired or already-used link - by far the most common real failure. */
export const AUTH_CALLBACK_EXPIRED =
  "That invitation link has expired or was already used. Ask your supervisor to send a new one.";

/** Anything else the link could carry. Never echoes the provider's error text. */
export const AUTH_CALLBACK_FAILED =
  "That sign-in link could not be used. Ask your supervisor to send a new invitation.";

/**
 * Classify the landing URL. `hash` and `search` are passed in (with or without their leading
 * "#"/"?") so this stays testable and free of window access.
 */
export function parseAuthCallback(hash: string, search: string): AuthCallback {
  const fragment = new URLSearchParams(stripLeading(hash, "#"));
  const query = new URLSearchParams(stripLeading(search, "?"));

  const error = fragment.get("error") ?? query.get("error");
  const errorCode = fragment.get("error_code") ?? query.get("error_code");
  if (error !== null || errorCode !== null) {
    return { kind: "error", reason: describeCallbackError(errorCode) };
  }

  if (fragment.get("access_token") !== null) {
    return { kind: "session", purpose: readPurpose(fragment.get("type")) };
  }
  // PKCE flow: the code is exchanged by supabase-js; the link type is not exposed in the URL.
  if (query.get("code") !== null) {
    return { kind: "session", purpose: readPurpose(query.get("type")) };
  }
  return { kind: "none" };
}

/** True when arriving on this link means the officer still has to choose a password. */
export function requiresPasswordSetup(callback: AuthCallback): boolean {
  return (
    callback.kind === "session" && (callback.purpose === "invite" || callback.purpose === "recovery")
  );
}

function describeCallbackError(errorCode: string | null): string {
  const code = (errorCode ?? "").toLowerCase();
  if (code.includes("expired") || code.includes("otp")) {
    return AUTH_CALLBACK_EXPIRED;
  }
  return AUTH_CALLBACK_FAILED;
}

function readPurpose(value: string | null): AuthCallbackPurpose {
  switch (value) {
    case "invite":
      return "invite";
    case "recovery":
      return "recovery";
    case "signup":
      return "signup";
    case "magiclink":
      return "magiclink";
    default:
      return "unknown";
  }
}

function stripLeading(value: string, prefix: string): string {
  return value.startsWith(prefix) ? value.slice(prefix.length) : value;
}

// Snapshot taken at module load - before createSupabaseClient runs and clears the fragment.
const INITIAL_AUTH_CALLBACK: AuthCallback =
  typeof window === "undefined"
    ? { kind: "none" }
    : parseAuthCallback(window.location.hash, window.location.search);

export function initialAuthCallback(): AuthCallback {
  return INITIAL_AUTH_CALLBACK;
}
